import React from "react";
import { motion } from "framer-motion";
import { Apple, ArrowUpRight, BadgeCheck, Clock3, Sparkles, Star } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export const ANDROID_APP_URL = process.env.REACT_APP_ANDROID_APP_URL
  || "https://play.google.com/store/apps/details?id=in.savvycampus.app";
export const IOS_APP_URL = process.env.REACT_APP_IOS_APP_URL || "";

function GooglePlayButton() {
  return (
    <a
      href={ANDROID_APP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download Savvy Campus on Google Play"
      className="group inline-flex h-[56px] items-center gap-3 rounded-xl bg-black px-4 text-white shadow-xl transition hover:-translate-y-0.5 hover:bg-zinc-900"
      data-testid="android-google-play-link"
    >
      <svg aria-hidden="true" viewBox="0 0 32 36" className="h-8 w-7 shrink-0">
        <path fill="#34A853" d="M1.8 1.2A4 4 0 0 0 .5 4.3v27.4a4 4 0 0 0 1.3 3.1l15.4-16.7L1.8 1.2Z" />
        <path fill="#FBBC04" d="m22.2 12.7-5 5.4 5.1 5.5 7-4a2 2 0 0 0 0-3.5l-7.1-3.4Z" />
        <path fill="#4285F4" d="M1.8 1.2 21.9 12.8l-4.7 5.3L1.8 1.2Z" />
        <path fill="#EA4335" d="m1.8 34.8 20.5-11.2-5.1-5.5L1.8 34.8Z" />
      </svg>
      <span className="text-left leading-none">
        <span className="block text-[9px] font-medium uppercase tracking-[0.08em] text-zinc-300">Get it on</span>
        <span className="mt-1 block text-lg font-semibold tracking-tight">Google Play</span>
      </span>
      <ArrowUpRight size={15} className="ml-1 text-zinc-500 transition group-hover:text-white" />
    </a>
  );
}

function AppStoreButton() {
  const content = (
    <>
      <Apple aria-hidden="true" className="h-8 w-8 shrink-0 fill-current" />
      <span className="text-left leading-none">
        <span className="block text-[9px] font-medium uppercase tracking-[0.08em] text-zinc-300">
          {IOS_APP_URL ? "Download on the" : "Coming soon to the"}
        </span>
        <span className="mt-1 block text-lg font-semibold tracking-tight">App Store</span>
      </span>
      {IOS_APP_URL ? (
        <ArrowUpRight size={15} className="ml-1 text-zinc-500 transition group-hover:text-white" />
      ) : (
        <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-violet-400/20 px-2 py-1 text-[8px] font-bold uppercase tracking-wider text-violet-200">
          <Clock3 size={10} /> Coming soon
        </span>
      )}
    </>
  );

  if (IOS_APP_URL) {
    return (
      <a
        href={IOS_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Download Savvy Campus on the App Store"
        className="group inline-flex h-[56px] items-center gap-3 rounded-xl bg-black px-4 text-white shadow-xl transition hover:-translate-y-0.5 hover:bg-zinc-900"
        data-testid="ios-app-store-link"
      >
        {content}
      </a>
    );
  }

  return (
    <div
      aria-label="Savvy Campus for iPhone is coming soon to the App Store"
      className="inline-flex h-[56px] items-center gap-3 rounded-xl bg-black px-4 text-white shadow-xl"
      data-testid="ios-app-store-coming-soon"
    >
      {content}
    </div>
  );
}

function PhonePreview() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 52, rotate: 4 }}
      whileInView={{ opacity: 1, y: 0, rotate: 2 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="relative mx-auto w-[246px] sm:w-[278px] lg:w-[294px]"
    >
      <div className="absolute -inset-16 rounded-full bg-fuchsia-950/35 blur-3xl" />
      <div className="relative rounded-[3.15rem] border-[7px] border-[#161619] bg-black p-[5px] shadow-[0_50px_100px_-24px_rgba(28,5,58,0.75)] ring-1 ring-white/35">
        <div className="relative aspect-[1280/2856] overflow-hidden rounded-[2.65rem] bg-black">
          <motion.img
            src="/images/app-preview/android-home.webp"
            alt="Savvy Campus Android home screen showing personalised student deals"
            className="absolute inset-0 h-full w-full object-cover"
            animate={{ opacity: [1, 1, 0, 0, 1] }}
            transition={{ duration: 10, times: [0, 0.42, 0.5, 0.92, 1], repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.img
            src="/images/app-preview/android-points.webp"
            alt="Savvy Campus Android Savvy Points rewards screen"
            className="absolute inset-0 h-full w-full object-cover"
            animate={{ opacity: [0, 0, 1, 1, 0] }}
            transition={{ duration: 10, times: [0, 0.42, 0.5, 0.92, 1], repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </div>

      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -left-16 top-[28%] hidden rounded-2xl border border-white/25 bg-black/90 p-3 text-white shadow-2xl backdrop-blur-xl sm:block"
      >
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-amber-300 text-black"><Sparkles size={14} /></div>
          <div><p className="text-[8px] uppercase tracking-wider text-zinc-500">Rewards live</p><p className="text-xs font-bold">410 Savvy Points</p></div>
        </div>
      </motion.div>

      <div className="absolute -right-8 bottom-[18%] hidden rounded-full border border-white/30 bg-white/90 px-3 py-1.5 text-[10px] font-extrabold text-purple-950 shadow-xl sm:block">
        HOME ↔ POINTS
      </div>
    </motion.div>
  );
}

export default function AndroidAppBanner() {
  return (
    <section className="relative px-4 py-16 sm:px-6 md:py-24" aria-labelledby="mobile-app-heading" data-testid="mobile-app-section">
      <motion.div
        initial={{ opacity: 0, y: 26 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.65 }}
        className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.25rem] border border-white/10 bg-[#f5f2e9] shadow-[0_45px_120px_-55px_rgba(168,85,247,0.5)]"
      >
        {/* Bezier split recreates the supplied reference's curved middle edge. */}
        <svg className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block" viewBox="0 0 1440 640" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="savvyAndroidPanel" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#c4b5fd" />
              <stop offset="48%" stopColor="#a78bfa" />
              <stop offset="100%" stopColor="#6ee7b7" />
            </linearGradient>
          </defs>
          <path d="M 694 0 C 792 126 830 245 790 326 C 748 412 746 520 694 640 L 1440 640 L 1440 0 Z" fill="url(#savvyAndroidPanel)" />
        </svg>

        <div className="absolute inset-x-0 bottom-0 h-[49%] bg-gradient-to-br from-violet-300 via-purple-400 to-emerald-300 lg:hidden" style={{ borderRadius: "50% 50% 0 0 / 11% 11% 0 0" }} />
        <div className="pointer-events-none absolute right-[-5%] top-[4%] hidden h-[92%] w-[45%] rounded-full border border-white/30 opacity-30 lg:block" />
        <div className="pointer-events-none absolute right-[18%] top-[8%] hidden h-20 w-20 rotate-12 rounded-3xl bg-white/10 lg:block" />

        <div className="relative grid min-h-[620px] items-center gap-8 px-6 py-12 sm:px-10 lg:grid-cols-[1.04fr_0.96fr] lg:px-16 lg:py-0">
          <div className="max-w-[610px] text-zinc-950 lg:pr-12">
            <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-purple-950/10 bg-purple-200/45 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.19em] text-purple-950">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-70" /><span className="relative h-2 w-2 rounded-full bg-emerald-500" /></span>
              Android live <span className="text-purple-950/35">•</span> iOS coming soon
            </div>
            <h2 id="mobile-app-heading" className="mt-6 font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-5xl lg:text-[3.6rem]">
              Get the free <span className="bg-violet-300 px-1.5">Savvy Campus app</span> for student deals, rewards and more.
            </h2>
            <p className="mt-5 max-w-xl text-sm leading-7 text-zinc-600 sm:text-base">Your digital student ID, personalised offers, nearby campus spots, saved deals and Savvy Points—live on Android today, with iPhone coming next.</p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <GooglePlayButton />
              <AppStoreButton />
              <div className="hidden items-center gap-3 sm:flex">
                <div className="rounded-xl border border-zinc-950/10 bg-white p-2 shadow-xl" aria-label="QR code to download Savvy Campus on Google Play">
                  <QRCodeSVG value={ANDROID_APP_URL} size={62} level="M" bgColor="#ffffff" fgColor="#050505" />
                </div>
                <div className="text-xs leading-5 text-zinc-500">Scan to<br /><span className="font-bold text-zinc-800">download now</span></div>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-xs font-medium text-zinc-600">
              <span className="inline-flex items-center gap-1.5"><BadgeCheck size={14} className="text-emerald-600" /> Android available now</span>
              <span className="inline-flex items-center gap-1.5"><Clock3 size={14} className="text-violet-600" /> iOS preparing for App Store release</span>
              <span className="inline-flex items-center gap-1.5"><Star size={14} className="text-amber-500" fill="currentColor" /> Made for Indian students</span>
            </div>
          </div>

          <div className="relative flex min-h-[500px] items-center justify-center pt-8 lg:min-h-[620px] lg:pt-0">
            <div className="absolute inset-x-[-20%] bottom-[-12%] top-[12%] rounded-full bg-purple-950/10 blur-3xl" />
            <PhonePreview />
          </div>
        </div>
      </motion.div>
    </section>
  );
}
